'use client';
import { forwardRef, useEffect, useId, useState, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';
import styles from './modal.module.scss';
import { CloseIcon } from '@/assets/icons/CloseIcon';
import { createPortal } from 'react-dom';
import { type ButtonProps, Button } from '@/ui-components/atoms/Button/Button';
import { Text } from '@/ui-components/atoms/Text/Text';

export interface ModalProps {
    children?: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    className?: string;
    pauseFocus?: boolean;
    maxWidth?: number | 'none';
    backgroundTransparent?: boolean;
    isOverflowHidden?: boolean;
    variant?: 'medium' | 'small' | 'video' | 'plancard';
    headline?: React.ReactNode;
    subhead?: React.ReactNode;
    paragraph?: React.ReactNode;
    ctas?: ButtonProps[];
    containerRef?: React.RefObject<HTMLElement | null>;
    dataLocalpagename?: string;
    contentClassName?: string;
    centeredLayout?: boolean;
    keepContentsMounted?: boolean;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
    (
        {
            open,
            onClose,
            children,
            className,
            maxWidth,
            backgroundTransparent,
            isOverflowHidden,
            variant,
            headline,
            subhead,
            paragraph,
            ctas,
            containerRef,
            dataLocalpagename,
            contentClassName,
            centeredLayout,
            keepContentsMounted,
        },
        ref,
    ) => {
        const [isClient, setIsClient] = useState(false);
        const descId = `desc-${useId()}`;
        const localRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

        useEffect(() => {
            setIsClient(true);
        }, []);

        useEffect(() => {
            const page = document.documentElement;

            const handleKeyUp = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose?.();
            };

            if (open) {
                document.addEventListener('keyup', handleKeyUp);
                page.classList.add('scrollLock');
            } else {
                page.classList.remove('scrollLock');
            }

            return () => {
                document.removeEventListener('keyup', handleKeyUp);
                page.classList.remove('scrollLock');
            };
        }, [open, onClose]);

        useEffect(() => {
            if (open && localRef.current) {
                if (dataLocalpagename) {
                    localRef.current.dispatchEvent(
                        new CustomEvent('modalopen', { bubbles: true, detail: { pagename: dataLocalpagename } }),
                    );
                }

                requestAnimationFrame(() => {
                    localRef.current!.scrollTop = 0;
                });
            } else if (!open && localRef.current) {
                if (dataLocalpagename) {
                    localRef.current.dispatchEvent(new CustomEvent('modalclose', { bubbles: true }));
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);

        if (!isClient && !keepContentsMounted) return null;
        const portalTarget = variant === 'plancard' || keepContentsMounted ? containerRef?.current : document.body;

        const modalClasses = cn(styles.modal, className, {
            [styles.opened]: open,
            [styles[variant ?? 'medium']]: variant,
            [styles.centered]: centeredLayout,
        });

        const containerClasses = cn(styles.container, {
            [styles.transparentBackground]: backgroundTransparent,
            [styles.overflowHidden]: isOverflowHidden,
        });

        return (
            <PortalIfNotKeepMounted
                keepContentsMounted={!!keepContentsMounted}
                portalTarget={portalTarget}
                containerRef={containerRef}
            >
                <div
                    className={modalClasses}
                    ref={localRef}
                    data-componenttype="modal"
                    data-localpagename={dataLocalpagename}
                >
                    <button
                        type="button"
                        className={styles.overlay}
                        aria-label="Close modal"
                        onClick={onClose}
                    />

                    {onClose && variant === 'video' && (
                        <button className={styles.closeX} aria-label="Close modal" onClick={onClose}>
                            <CloseIcon />
                        </button>
                    )}

                    <dialog
                        open={open}
                        className={containerClasses}
                        style={{ maxWidth: maxWidth || '' }}
                        aria-hidden={!open}
                        aria-modal="true"
                        aria-describedby={descId}
                    >
                        {onClose && variant !== 'video' && (
                            <button className={styles.closeX} aria-label="Close modal" onClick={onClose}>
                                <CloseIcon />
                            </button>
                        )}
                        <div
                            className={cn(styles.content, contentClassName)}
                            id={descId}
                        >
                            {(headline || subhead || paragraph) && (
                                <div className={styles.headerGroup}>
                                    {headline && (
                                        <Text
                                            tag="h1"
                                            variant={variant === 'plancard' ? 'h6' : variant === 'small' ? 'h5' : 'h4'}
                                            className={styles.headline}
                                        >
                                            {headline}
                                        </Text>
                                    )}
                                    {subhead && (
                                        <Text tag="div" className={styles.subhead} variant="text-medium">
                                            {subhead}
                                        </Text>
                                    )}
                                    {paragraph && (
                                        <Text tag="div" className={styles.paragraph} variant="text-medium">
                                            {paragraph}
                                        </Text>
                                    )}
                                </div>
                            )}

                            {children}

                            {!!ctas?.length && (
                                <div className={styles.ctaGroup}>
                                    {ctas.map((cta, index) => (
                                        <Button key={index} {...cta} type="button" size="small" />
                                    ))}
                                </div>
                            )}
                        </div>
                    </dialog>
                </div>
            </PortalIfNotKeepMounted>
        );
    },
);
Modal.displayName = 'Modal';

interface PortalIfNotKeepMountedProps {
    children: React.ReactNode;
    keepContentsMounted: boolean;
    portalTarget: HTMLElement | null | undefined;
    containerRef?: React.RefObject<HTMLElement | null>;
}
function PortalIfNotKeepMounted({
    children,
    keepContentsMounted,
    portalTarget,
    containerRef,
}: PortalIfNotKeepMountedProps): React.ReactNode {
    if (keepContentsMounted) {
        return children;
    }

    return portalTarget ? createPortal(children, containerRef?.current || document.body) : null;
}
